import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { useResponsive } from '../utils/useResponsive';
import { parseDateOnlyLocal } from '../utils/helpers';
import { isStageEnabled, isVehicleWorkTypeVisible } from '../utils/enabledStages';

const MAX_DAY_ITEMS_VISIBLE = 3;
/** Week view: same cap so overflow goes into "+N more" */
const MAX_WEEK_DAY_ITEMS_VISIBLE = 3;

const Calendar = ({ onOpenVehicleDetail, onBookVehicle }) => {
  const { vehicles, updateVehicle, garageInfo } = useApp();
  const { isMobile, isTablet } = useResponsive();
  const isMobileView = isMobile || isTablet;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month'); // 'month', 'week', 'day'
  const [isDragging, setIsDragging] = useState(false);
  const [viewMoreState, setViewMoreState] = useState(null); // { dayKey, remaining, anchorRect }
  const [expandedWeekDayIndex, setExpandedWeekDayIndex] = useState(null); // mobile week: which day section is open (0-6), null = all collapsed
  const [expandedMonthDayKey, setExpandedMonthDayKey] = useState(null); // mobile month: which day is open (YYYY-MM-DD), null = all collapsed
  const dragTimeoutRef = useRef(null);
  const viewMorePopoverRef = useRef(null);

  // When switching to week view (mobile), open the day that is today, or first day
  useEffect(() => {
    if (view === 'week') {
      const weekDays = calendarDays.filter(d => d.type === 'day');
      if (weekDays.length > 0) {
        const todayIdx = weekDays.findIndex(d => d.isToday);
        setExpandedWeekDayIndex(todayIdx >= 0 ? todayIdx : 0);
      }
    }
    // Intentionally omit calendarDays — including it would re-expand on every vehicle change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentDate]);

  // When switching to month view (mobile), open today's section if in current month
  useEffect(() => {
    if (view === 'month') {
      const monthDays = calendarDays.filter(d => d.type === 'day' && !d.isOtherMonth);
      const todayDay = monthDays.find(d => d.isToday);
      if (todayDay?.date) {
        const y = todayDay.date.getFullYear();
        const m = String(todayDay.date.getMonth() + 1).padStart(2, '0');
        const day = String(todayDay.date.getDate()).padStart(2, '0');
        setExpandedMonthDayKey(`${y}-${m}-${day}`);
      } else {
        setExpandedMonthDayKey(null);
      }
    }
    // Intentionally omit calendarDays — including it would re-expand on every vehicle change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentDate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  // Close view-more popover on click outside / Escape
  useEffect(() => {
    if (!viewMoreState) return;
    const handleClickOutside = (e) => {
      if (viewMorePopoverRef.current && !viewMorePopoverRef.current.contains(e.target) && !e.target.closest('.calendar-view-more-trigger')) {
        setViewMoreState(null);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setViewMoreState(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [viewMoreState]);

  useEffect(() => {
    if (!viewMoreState || !viewMorePopoverRef.current) return;
    const el = viewMorePopoverRef.current;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    let left = viewMoreState.anchorRect?.left ?? 0;
    let top = (viewMoreState.anchorRect?.bottom ?? 0) + 6;
    if (left + rect.width > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - rect.width - pad);
    }
    if (left < pad) left = pad;
    if (top + rect.height > window.innerHeight - pad) {
      top = Math.max(pad, (viewMoreState.anchorRect?.top ?? 0) - rect.height - 6);
    }
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
  }, [viewMoreState]);

  const monthYear = currentDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const weekYear = (() => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    const startStr = startOfWeek.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const endStr = endOfWeek.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  })();
  const dayYear = currentDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Discrete events only: drop-off and collection days (not every day the car is on site).
  const getCalendarDayEvents = useMemo(() => {
    const events = [];
    vehicles.forEach((veh) => {
      if (veh.deletedAt) return;
      if (!isVehicleWorkTypeVisible(garageInfo, veh)) return;

      const arrival = parseDateOnlyLocal(veh.receivedDate || veh.dropOffDate);
      if (arrival) {
        arrival.setHours(0, 0, 0, 0);
        events.push({
          vehicle: veh,
          date: arrival,
          type: 'arrival',
          daysText: 'Drop-off',
          status: veh.status || 'In Progress'
        });
      }

      const collection = parseDateOnlyLocal(veh.collectionDate);
      if (collection) {
        collection.setHours(0, 0, 0, 0);
        events.push({
          vehicle: veh,
          date: collection,
          type: 'collection',
          daysText: 'Collection',
          status: veh.status || 'In Progress'
        });
      }
    });
    return events;
  }, [vehicles, garageInfo]);

  const calendarDays = useMemo(() => {
    const eventsForDate = (day) => {
      const dayTime = day.getTime();
      return getCalendarDayEvents.filter((event) => event.date.getTime() === dayTime);
    };

    if (view === 'day') {
      const day = new Date(currentDate);
      day.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return [{
        type: 'day',
        date: day,
        dayNumber: day.getDate(),
        isOtherMonth: false,
        isToday: day.getTime() === today.getTime(),
        vehicles: eventsForDate(day)
      }];
    }

    if (view === 'week') {
      const startDate = new Date(currentDate);
      startDate.setDate(startDate.getDate() - startDate.getDay());
      startDate.setHours(0, 0, 0, 0);

      const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const days = [];
      dayHeaders.forEach((label) => {
        days.push({ type: 'header', label });
      });

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const loopDate = new Date(startDate);

      for (let i = 0; i < 7; i++) {
        const compareDate = new Date(loopDate);
        compareDate.setHours(0, 0, 0, 0);
        days.push({
          type: 'day',
          date: new Date(loopDate),
          dayNumber: loopDate.getDate(),
          isOtherMonth: false,
          isToday: compareDate.getTime() === today.getTime(),
          vehicles: eventsForDate(compareDate)
        });
        loopDate.setDate(loopDate.getDate() + 1);
      }

      return days;
    }

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const days = [];
    dayHeaders.forEach((label) => {
      days.push({ type: 'header', label });
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const loopDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      const compareDate = new Date(loopDate);
      compareDate.setHours(0, 0, 0, 0);
      days.push({
        type: 'day',
        date: new Date(loopDate),
        dayNumber: loopDate.getDate(),
        isOtherMonth: loopDate.getMonth() !== month,
        isToday: compareDate.getTime() === today.getTime(),
        vehicles: eventsForDate(compareDate)
      });
      loopDate.setDate(loopDate.getDate() + 1);
    }

    return days;
  }, [currentDate, view, getCalendarDayEvents]);

  const handleVehicleClick = (veh) => {
    // Don't open detail modal if we just finished dragging
    if (isDragging) {
      return;
    }
    if (onOpenVehicleDetail) {
      const vehicleIndex = vehicles.findIndex(v => v.reg === veh.reg);
      if (vehicleIndex !== -1) {
        onOpenVehicleDetail(vehicleIndex);
      }
    }
  };

  const handleDragStart = (e, veh) => {
    if (!veh?.reg) return;
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', veh.reg);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ reg: veh.reg }));
    e.stopPropagation();
    // Add visual feedback during drag
    e.currentTarget.style.opacity = '0.5';
    e.currentTarget.style.cursor = 'grabbing';
  };

  const handleDragEnd = (e) => {
    // Restore opacity and cursor after drag ends
    e.currentTarget.style.opacity = '1';
    e.currentTarget.style.cursor = 'grab';
    // Use setTimeout to allow drop event to complete first
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    dragTimeoutRef.current = setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    e.currentTarget.classList.add('droppable-hover');
  };

  const handleDragLeave = (e) => {
    // Only remove hover class if we're actually leaving the drop zone
    // (not just moving to a child element)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.classList.remove('droppable-hover');
    }
  };

  const handleDrop = (e, dropDate) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('droppable-hover');
    
    let reg = e.dataTransfer.getData('text/plain');
    if (!reg) {
      try {
        const data = e.dataTransfer.getData('application/json');
        if (data) {
          const parsed = JSON.parse(data);
          reg = parsed?.reg;
        }
      } catch (_) { /* ignore */ }
    }
    if (!reg) {
      console.warn('No registration found in drag data');
      setIsDragging(false);
      return;
    }
    
    // Find the vehicle in the current vehicles array
    const idx = vehicles.findIndex(v => v.reg === reg);
    if (idx === -1) {
      console.warn(`Vehicle with registration ${reg} not found`);
      setIsDragging(false);
      return;
    }
    
    const vehicle = vehicles[idx];
    if (!vehicle || !vehicle.reg) {
      console.warn('Invalid vehicle data');
      setIsDragging(false);
      return;
    }
    
    // Format the drop date as YYYY-MM-DD using local timezone
    const year = dropDate.getFullYear();
    const month = String(dropDate.getMonth() + 1).padStart(2, '0');
    const day = String(dropDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    // Update the vehicle's receivedDate or dropOffDate
    const updates = {
      ...vehicle,
      receivedDate: dateStr,
      dropOffDate: dateStr
    };
    
    // If vehicle was collected, clear collection date since we're moving it
    if (vehicle.collected) {
      updates.collected = false;
      updates.collectionDate = null;
    }
    
    console.log(`Moving vehicle ${reg} to date ${dateStr}`);
    
    try {
      updateVehicle(idx, updates);
    } catch (error) {
      console.error('Error updating vehicle:', error);
    }
    
    setIsDragging(false);
  };

  const getStatusClass = (status) => {
    return status.toLowerCase().replace(/\s+/g, '-');
  };

  const handlePrev = () => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const getDisplayDate = () => {
    if (view === 'day') return dayYear;
    if (view === 'week') return weekYear;
    return monthYear;
  };

  return (
    <div className={`calendar-container ${view === 'week' ? 'calendar-container--week' : ''}`}>
      <div className="calendar-header">
        <button 
          type="button" 
          className="calendar-nav-btn"
          onClick={handlePrev}
        >
          ‹
        </button>
        <h4>{getDisplayDate()}</h4>
        <button 
          type="button" 
          className="calendar-nav-btn"
          onClick={handleNext}
        >
          ›
        </button>
        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '4px', backgroundColor: 'rgba(79, 141, 243, 0.1)', borderRadius: '8px', padding: '4px' }}>
            <button
              type="button"
              onClick={() => setView('day')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: view === 'day' ? 'var(--primary)' : 'transparent',
                color: view === 'day' ? '#fff' : 'var(--text-dark)',
                fontSize: '13px',
                fontWeight: view === 'day' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Day
            </button>
            <button
              type="button"
              onClick={() => setView('week')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: view === 'week' ? 'var(--primary)' : 'transparent',
                color: view === 'week' ? '#fff' : 'var(--text-dark)',
                fontSize: '13px',
                fontWeight: view === 'week' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Week
            </button>
            <button
              type="button"
              onClick={() => setView('month')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: view === 'month' ? 'var(--primary)' : 'transparent',
                color: view === 'month' ? '#fff' : 'var(--text-dark)',
                fontSize: '13px',
                fontWeight: view === 'month' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Month
            </button>
          </div>
        </div>
      </div>
      {/* Day view (mobile only): full-width list of vehicles for the selected day */}
      {view === 'day' && isMobileView && calendarDays.length > 0 && calendarDays[0].type === 'day' && (
        <div className="calendar-day-view">
          <div className="calendar-day-view-list">
            {calendarDays[0].vehicles.length === 0 ? (
              <div className="calendar-day-view-empty">
                No vehicles for this day. Tap the day to add a booking.
              </div>
            ) : (
              calendarDays[0].vehicles.map((vehItem, i) => {
                const statusClass = getStatusClass(vehItem.status || 'In Progress');
                const categoryLabel = vehItem.status || 'In Progress';
                return (
                  <button
                    key={vehItem.vehicle?.reg ? `day-${vehItem.type}-${vehItem.vehicle.reg}-${i}` : `day-${i}`}
                    type="button"
                    className={`calendar-day-view-item status-${statusClass}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (vehItem.vehicle) handleVehicleClick(vehItem.vehicle);
                    }}
                  >
                    <span className="calendar-day-view-category-tag">{categoryLabel}</span>
                    <div className="calendar-day-view-row">
                      <span className="calendar-day-view-reg">{vehItem.vehicle?.reg ?? '—'}</span>
                      <span className="calendar-day-view-meta">{vehItem.daysText}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
      {/* Week view (mobile only): stacked day sections, collapsible – today open by default */}
      {view === 'week' && isMobileView && (() => {
        const weekDays = calendarDays.filter(d => d.type === 'day');
        if (weekDays.length === 0) return null;
        const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return (
          <div className="calendar-week-view">
            <div className="calendar-week-view-list">
              {weekDays.map((day, dayIdx) => {
                const dateLabel = day.date ? day.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : dayLabels[dayIdx];
                const isExpanded = expandedWeekDayIndex === dayIdx;
                const count = day.vehicles.length;
                const toggleDay = () => setExpandedWeekDayIndex(isExpanded ? null : dayIdx);
                return (
                  <section key={day.date ? day.date.toISOString() : `week-day-${dayIdx}`} className={`calendar-week-day-section ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    <button
                      type="button"
                      className={`calendar-week-day-header ${day.isToday ? 'today' : ''}`}
                      onClick={toggleDay}
                      aria-expanded={isExpanded}
                      aria-label={`${dateLabel}, ${count} vehicles. ${isExpanded ? 'Collapse' : 'Expand'}`}
                    >
                      <span className="calendar-week-day-header-label">{dateLabel}</span>
                      <span className="calendar-week-day-header-meta">
                        {count === 0 ? 'No vehicles' : count === 1 ? '1 vehicle' : `${count} vehicles`}
                      </span>
                      <span className="calendar-week-day-header-chevron" aria-hidden>{isExpanded ? '▼' : '▶'}</span>
                    </button>
                    {isExpanded && (
                      <div className="calendar-week-day-list">
                        {day.vehicles.length === 0 ? (
                          <div className="calendar-day-view-empty">No vehicles</div>
                        ) : (
                          day.vehicles.map((vehItem, i) => {
                            const statusClass = getStatusClass(vehItem.status || 'In Progress');
                            const categoryLabel = vehItem.status || 'In Progress';
                            if (vehItem.type !== 'bar') {
                              return (
                                <button
                                  key={vehItem.vehicle?.reg ? `w-${vehItem.vehicle.reg}-${i}` : `w-${dayIdx}-${i}`}
                                  type="button"
                                  className={`calendar-day-view-item status-${statusClass}`}
                                  onClick={(e) => { e.stopPropagation(); if (vehItem.vehicle) handleVehicleClick(vehItem.vehicle); }}
                                >
                                  <span className="calendar-day-view-category-tag">{categoryLabel}</span>
                                  <div className="calendar-day-view-row">
                                    <span className="calendar-day-view-reg">{vehItem.vehicle?.reg ?? '—'}</span>
                                    <span className="calendar-day-view-meta">{vehItem.daysText}</span>
                                  </div>
                                </button>
                              );
                            }
                            return (
                              <button
                                key={vehItem.vehicle?.reg ? `w-bar-${vehItem.vehicle.reg}-${i}` : `w-bar-${dayIdx}-${i}`}
                                type="button"
                                className={`calendar-day-view-item calendar-day-view-item-bar status-${statusClass}`}
                                onClick={(e) => { e.stopPropagation(); if (vehItem.vehicle) handleVehicleClick(vehItem.vehicle); }}
                              >
                                <span className="calendar-day-view-category-tag">{categoryLabel}</span>
                                <div className="calendar-day-view-row">
                                  <span className="calendar-day-view-reg">{vehItem.vehicle?.reg ?? '—'}</span>
                                  <span className="calendar-day-view-meta">In progress</span>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
        );
      })()}
      {/* Month view (mobile only): stacked day sections for current month, collapsible – today open by default */}
      {view === 'month' && isMobileView && (() => {
        const monthDays = calendarDays
          .filter(d => d.type === 'day' && !d.isOtherMonth)
          .sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));
        if (monthDays.length === 0) return null;
        const getDateKey = (d) => {
          if (!d.date) return '';
          const y = d.date.getFullYear();
          const m = String(d.date.getMonth() + 1).padStart(2, '0');
          const day = String(d.date.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        };
        return (
          <div className="calendar-month-view">
            <div className="calendar-month-view-list">
              {monthDays.map((day) => {
                const dateKey = getDateKey(day);
                const dateLabel = day.date ? day.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
                const isExpanded = expandedMonthDayKey === dateKey;
                const count = day.vehicles.length;
                const toggleDay = () => setExpandedMonthDayKey(isExpanded ? null : dateKey);
                return (
                  <section key={dateKey || day.dayNumber} className={`calendar-month-day-section ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    <button
                      type="button"
                      className={`calendar-month-day-header ${day.isToday ? 'today' : ''}`}
                      onClick={toggleDay}
                      aria-expanded={isExpanded}
                      aria-label={`${dateLabel}, ${count} vehicles. ${isExpanded ? 'Collapse' : 'Expand'}`}
                    >
                      <span className="calendar-month-day-header-label">{dateLabel}</span>
                      <span className="calendar-month-day-header-meta">
                        {count === 0 ? 'No vehicles' : count === 1 ? '1 vehicle' : `${count} vehicles`}
                      </span>
                      <span className="calendar-month-day-header-chevron" aria-hidden>{isExpanded ? '▼' : '▶'}</span>
                    </button>
                    {isExpanded && (
                      <div className="calendar-month-day-list">
                        {day.vehicles.length === 0 ? (
                          <div className="calendar-day-view-empty">No vehicles</div>
                        ) : (
                          day.vehicles.map((vehItem, i) => {
                            const statusClass = getStatusClass(vehItem.status || 'In Progress');
                            const categoryLabel = vehItem.status || 'In Progress';
                            if (vehItem.type !== 'bar') {
                              return (
                                <button
                                  key={vehItem.vehicle?.reg ? `m-${vehItem.vehicle.reg}-${i}` : `m-${dateKey}-${i}`}
                                  type="button"
                                  className={`calendar-day-view-item status-${statusClass}`}
                                  onClick={(e) => { e.stopPropagation(); if (vehItem.vehicle) handleVehicleClick(vehItem.vehicle); }}
                                >
                                  <span className="calendar-day-view-category-tag">{categoryLabel}</span>
                                  <div className="calendar-day-view-row">
                                    <span className="calendar-day-view-reg">{vehItem.vehicle?.reg ?? '—'}</span>
                                    <span className="calendar-day-view-meta">{vehItem.daysText}</span>
                                  </div>
                                </button>
                              );
                            }
                            return (
                              <button
                                key={vehItem.vehicle?.reg ? `m-bar-${vehItem.vehicle.reg}-${i}` : `m-bar-${dateKey}-${i}`}
                                type="button"
                                className={`calendar-day-view-item calendar-day-view-item-bar status-${statusClass}`}
                                onClick={(e) => { e.stopPropagation(); if (vehItem.vehicle) handleVehicleClick(vehItem.vehicle); }}
                              >
                                <span className="calendar-day-view-category-tag">{categoryLabel}</span>
                                <div className="calendar-day-view-row">
                                  <span className="calendar-day-view-reg">{vehItem.vehicle?.reg ?? '—'}</span>
                                  <span className="calendar-day-view-meta">In progress</span>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
        );
      })()}
      {(view !== 'day' || !isMobileView) && (view !== 'week' || !isMobileView) && (view !== 'month' || !isMobileView) && (
      <div className={`calendar-grid ${view === 'week' ? 'calendar-grid--week' : ''}`} id="calendarGrid">
        {calendarDays.map((day, idx) => {
          if (day.type === 'header') {
            return (
              <div key={`header-${day.label}`} className="calendar-day-header">
                {day.label}
              </div>
            );
          }

          const maxVisible =
            view === 'week' ? MAX_WEEK_DAY_ITEMS_VISIBLE : MAX_DAY_ITEMS_VISIBLE;

          const handleDayClick = (e) => {
            // Only open booking form if clicking on the day itself or day number, not on vehicles
            const target = e.target;
            const isVehicleClick = target.closest('.calendar-vehicle') || target.closest('.calendar-vehicle-bar');
            
            if (!isVehicleClick && onBookVehicle) {
              onBookVehicle();
            }
          };

          const dayKey = day.date ? `day-${day.date.toISOString()}` : `day-${idx}`;
          const visibleItems = day.vehicles.slice(0, maxVisible);
          const overflowCount = day.vehicles.length - maxVisible;
          const hasOverflow = overflowCount > 0;
          const remainingItems = hasOverflow ? day.vehicles.slice(maxVisible) : [];

          const openViewMore = (e) => {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            const dayLabel = day.date
              ? day.date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
              : 'This day';
            setViewMoreState({
              dayKey,
              dayLabel,
              remaining: remainingItems,
              totalCount: day.vehicles.length,
              anchorRect: rect
            });
          };

          return (
            <div
              key={dayKey}
              className={`calendar-day ${day.isOtherMonth ? 'other-month' : ''} ${day.isToday ? 'today' : ''} ${day.vehicles.length === 0 ? 'no-bookings' : ''}`}
              onClick={handleDayClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, day.date)}
              style={{ cursor: 'pointer' }}
            >
              <div className="calendar-day-number">{day.dayNumber}</div>
              {visibleItems.map((vehItem, vehIdx) => {
                const statusClass = getStatusClass(vehItem.status);
                const vehicleKey = vehItem.vehicle?.reg
                  ? `vehicle-${vehItem.type}-${vehItem.vehicle.reg}-${dayKey}-${vehIdx}`
                  : `vehicle-${vehItem.type}-${vehIdx}-${dayKey}`;
                return (
                  <div
                    key={vehicleKey}
                    className={`calendar-vehicle status-${statusClass}`}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, vehItem.vehicle)}
                    onDragEnd={handleDragEnd}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVehicleClick(vehItem.vehicle);
                    }}
                    style={{ cursor: 'grab' }}
                  >
                    <div className="calendar-vehicle-reg">{vehItem.vehicle.reg}</div>
                    <div className="calendar-vehicle-counter">{vehItem.daysText}</div>
                  </div>
                );
              })}
              {hasOverflow && (
                <button
                  type="button"
                  className="calendar-view-more-trigger"
                  onClick={openViewMore}
                  aria-label={`View ${overflowCount} more`}
                  title={`View ${overflowCount} more`}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="19" cy="12" r="1" />
                    <circle cx="5" cy="12" r="1" />
                  </svg>
                  <span>+{overflowCount} more</span>
                </button>
              )}
            </div>
          );
        })}
      </div>
      )}
      {(view !== 'day' && (view !== 'week' || !isMobileView) && (view !== 'month' || !isMobileView)) && viewMoreState && viewMoreState.remaining.length > 0 && (
        <div
          ref={viewMorePopoverRef}
          className="calendar-view-more-popover"
          style={{
            position: 'fixed',
            left: viewMoreState.anchorRect?.left ?? 0,
            top: (viewMoreState.anchorRect?.bottom ?? 0) + 6,
            zIndex: 1000
          }}
          role="dialog"
          aria-label={`More vehicles for ${viewMoreState.dayLabel || 'this day'}`}
        >
          <div className="calendar-view-more-popover-header">
            <div className="calendar-view-more-popover-heading">
              <span className="calendar-view-more-popover-title">
                {viewMoreState.remaining.length} more
              </span>
              {viewMoreState.dayLabel && (
                <span className="calendar-view-more-popover-subtitle">{viewMoreState.dayLabel}</span>
              )}
            </div>
            <button
              type="button"
              className="calendar-view-more-popover-close"
              onClick={(e) => {
                e.stopPropagation();
                setViewMoreState(null);
              }}
              aria-label="Close more vehicles"
            >
              ×
            </button>
          </div>
          <div className="calendar-view-more-popover-list">
            {viewMoreState.remaining.map((vehItem, i) => {
              const status = vehItem.status || vehItem.vehicle?.status || 'In Progress';
              const statusClass = getStatusClass(status);
              return (
                <button
                  key={vehItem.vehicle?.reg ? `more-${vehItem.type}-${vehItem.vehicle.reg}-${i}` : `more-${i}`}
                  type="button"
                  className={`calendar-view-more-popover-item status-${statusClass}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (vehItem.vehicle) handleVehicleClick(vehItem.vehicle);
                    setViewMoreState(null);
                  }}
                >
                  <span className="calendar-view-more-reg">{vehItem.vehicle?.reg ?? '—'}</span>
                  <span className="calendar-view-more-days">{vehItem.daysText || status}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="calendar-legend">
        {isStageEnabled(garageInfo, 'Bodyshop') && (
        <div className="legend-item">
          <div className="legend-color bodyshop"></div>
          <span>Bodyshop</span>
        </div>
        )}
        {isStageEnabled(garageInfo, 'MOT') && (
        <div className="legend-item">
          <div className="legend-color mot"></div>
          <span>MOT</span>
        </div>
        )}
        {isStageEnabled(garageInfo, 'Tyres') && (
        <div className="legend-item">
          <div className="legend-color tyres"></div>
          <span>Tyres</span>
        </div>
        )}
        <div className="legend-item">
          <div className="legend-color in-progress"></div>
          <span>In Progress</span>
        </div>
        {isStageEnabled(garageInfo, 'Sales') && (
        <div className="legend-item">
          <div className="legend-color sales"></div>
          <span>Sales</span>
        </div>
        )}
      </div>
    </div>
  );
};

export default Calendar;
